import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";

import tsLogo from "../../assets/svg/ts-logo.svg";
import logoCollapsed from "../../assets/svg/ts-logo-condensed.svg";
import { makeStyles } from "../../theme/theme";
import type { RootState } from "../../store/state/rootReducer";
import { getInitials } from "../utils/util";
import type { AppDispatch } from "../../store/state/rootStore";
import { getAllOrganizations } from "../../store/actions/organizations";

// Deliberately quicker than the sidebar's own 300ms width transition — the
// logo swap read as "off" when tied 1:1 to it, so it fades out/in on its own,
// snappier beat instead.
const HALF_DURATION_MS = 90;

const useStyles = makeStyles<{
  isCustomLogo: boolean;
  isExpanded: boolean;
  isTransitioning: boolean;
  isExpandTarget: boolean;
}>()((theme, { isCustomLogo, isExpanded, isTransitioning, isExpandTarget }) => {
  // ease-out (decelerate) when settling into the expanded state, ease-in
  // (accelerate) when collapsing away — matches the drawer's own width transition.
  const SIDEBAR_EASING = isExpandTarget ? theme.transitions.easing.easeOut : theme.transitions.easing.easeIn;

  return {
    logo: {
      [theme.breakpoints.down("sm")]: {
        maxWidth: "50%",
      },
      width: isCustomLogo ? "150px" : "116px",
      height: "auto",
      objectFit: "contain",
    },
    logoCollapsed: {
      [theme.breakpoints.down("sm")]: {
        maxWidth: "10%",
      },
      width: "35px",
      height: "20px",
    },
    initials: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "12px 6px 12px 7px",
      backgroundColor: "#fff",
      borderRadius: "8px",
      minWidth: "40px",
      maxWidth: "40px",
      minHeight: "40px",
      alignItems: "center",
      justifyContent: "center",
    },
    logoContainer: {
      display: "flex",
      justifyContent: !isExpanded ? "center" : isCustomLogo ? "center" : "flex-start",
      padding: "24px 16px 16px 16px",
      cursor: "pointer",
      maxHeight: "198px",
      minHeight: "65px",
    },
    logoFade: {
      opacity: isTransitioning ? 0 : 1,
      transition: `opacity ${HALF_DURATION_MS}ms ${SIDEBAR_EASING}`,
    },
  };
});

interface OrganizationLogoProps {
  isExpanded: boolean;
}

export const OrganizationLogo = ({ isExpanded }: OrganizationLogoProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const organizationLogo = useSelector((state: RootState) => state.organizations?.organizationLogo);
  const organizations = useSelector((state: RootState) => state.organizations?.organizations);

  const [isTransitioning, setIsTransitioning] = useState(true);
  // mirrors `isExpanded`, but only updates once the fade-out finishes, so the
  // logo swaps size/asset while fully invisible instead of flashing the new
  // one before it starts fading
  const [displayedExpanded, setDisplayedExpanded] = useState(isExpanded);

  const orgCode = Object.keys(organizations)[0];
  const organization = organizations[orgCode];
  const logoColl = organization?.logoUrl && getInitials(organization.organization_name);
  const isOrgBrandingFlagOn = useSelector((state: RootState) => state.featureFlags?.flags?.enableOrgBranding);
  const isCustomLogo = isOrgBrandingFlagOn && !!organizationLogo;
  const { classes } = useStyles({
    isCustomLogo,
    isExpanded: displayedExpanded,
    isTransitioning,
    isExpandTarget: isExpanded,
  });

  useEffect(() => {
    if (!organizationLogo) {
      dispatch(getAllOrganizations());
    }
  }, [organizationLogo]);

  useEffect(() => {
    if (isExpanded === displayedExpanded) {
      return undefined;
    }
    setIsTransitioning(true);
    const timeoutId = setTimeout(() => {
      setDisplayedExpanded(isExpanded);
      setIsTransitioning(false);
    }, HALF_DURATION_MS);

    return () => clearTimeout(timeoutId);
  }, [isExpanded, displayedExpanded]);

  // initial reveal once the component mounts
  useEffect(() => {
    const timeoutId = setTimeout(() => setIsTransitioning(false), HALF_DURATION_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  const logoRender = () => {
    if (isOrgBrandingFlagOn && organization?.logoUrl) {
      return displayedExpanded ? (
        <img
          className={classes.logo}
          src={organizationLogo}
          style={{ borderRadius: "8px" }}
          alt={organization?.organization_name ?? "Organization logo"}
        />
      ) : (
        <Box className={classes.initials}>
          <Typography color="#606971" fontSize="13px" fontWeight={700} lineHeight="16px">
            {logoColl}
          </Typography>
        </Box>
      );
    }

    if (!isOrgBrandingFlagOn || (isOrgBrandingFlagOn && !organization?.logoUrl)) {
      return (
        <img
          className={displayedExpanded ? classes.logo : classes.logoCollapsed}
          src={displayedExpanded ? tsLogo : logoCollapsed}
          alt="TheirStory"
        />
      );
    }

    return null;
  };

  return (
    <>
      <Box className={classes.logoContainer} onClick={() => navigate("/")}>
        <Box className={classes.logoFade}>{logoRender()}</Box>
      </Box>
    </>
  );
};
